<script setup lang="ts">
import { RouterView } from 'vue-router';
import LoadingLogo from './components/LoadingLogo.vue';
import LanguageSwitcher from './components/LanguageSwitcher.vue';
import LocalizedMessage from './components/LocalizedMessage.vue';
import MockApiConfigurator from './components/development/MockApiConfigurator.vue';
import ApiResponseViewer from './components/development/ApiResponseViewer.vue';
import DebugTools from './components/development/DebugTools.vue';
import { ref, onMounted } from 'vue';
import { useUserStore } from './stores/user';
import { useAuthStore } from './stores/auth';
import { getHttpClient } from './services/httpClientProvider';
import { fetchClientInstanceId } from '@/utils/clientInstance';
import PreviewBanner from './components/PreviewBanner.vue';
import TestDomainBanner from './components/TestDomainBanner.vue';
import Toast from 'primevue/toast';
import BravoToast from '@services/ui-component-library/components/BravoToast.vue';
import Message from 'primevue/message';

import { useOnlineStatus } from './composables/useOnlineStatus';
import { useServerStatus } from './composables/useServerStatus';
import { mfeLoader } from './services/MFELoaderService';
import { useRouter } from 'vue-router';
import { usePageHistoryTracker } from './composables/usePageHistoryTracker';
import { usePageHistoryStore } from '@/modules/search/stores/pageHistory';
import { useToast } from 'primevue/usetoast';
import { useGlobalCommWindows } from '@/composables/useCommWindows';
import { useIssuesAPI } from '@/composables/services/useIssuesAPI';
import { useCommsAPI } from '@/composables/services/useCommsAPI';

const userStore = useUserStore();
const authStore = useAuthStore();
const apiViewerRef = ref();
const loading = ref(true);
const { isOnline } = useOnlineStatus();
const { hasServerError } = useServerStatus();
const router = useRouter();
const { initializeTracking } = usePageHistoryTracker();
const pageHistoryStore = usePageHistoryStore();
const toast = useToast();

// Initialize comm windows and API composables
const commWindows = useGlobalCommWindows();
const issuesAPI = useIssuesAPI();
const commsAPI = useCommsAPI();

// Function to restore comm windows from localStorage
const restoreCommWindows = async (): Promise<void> => {
    try {
        await commWindows.restoreCommWindowsFromStorage(
            // Fetch issue function
            async (issueId: string) => {
                try {
                    return await issuesAPI.fetchIssue(issueId);
                } catch (error) {
                    console.error('💾 App: Failed to fetch issue:', issueId, error);
                    return null;
                }
            },
            // Fetch comm function
            async (commId: string) => {
                try {
                    const commData = await commsAPI.getComm({ id: commId });
                    // Convert CommData to AvailableComm format
                    return {
                        id: commData.id,
                        object: commData.object,
                        object_id: commData.object_id,
                        object_scope: commData.object_scope,
                        comm_type: commData.comm_type,
                        comm_label: commData.comm_label,
                        comm_status: commData.comm_status,
                        comm_state: commData.comm_state,
                        object_source: commData.object_source,
                        billing_status: commData.billing_status,
                        title: commData.title,
                        subtitle: commData.subtitle,
                        duration_plus: commData.duration_plus,
                        duration: commData.duration,
                        user_message_cnt: commData.user_message_cnt,
                        external_rpid: commData.external_rpid,
                        external_lpid: commData.external_lpid,
                        external_id: commData.external_id,
                        external_status: commData.external_status,
                        created: commData.created,
                        updated: commData.updated,
                        occupied: commData.occupied,
                        completed: commData.completed,
                        c__status: commData.c__status,
                        c__state: commData.c__state,
                        _highlighted: false,
                        _highlightmap: {},
                        c__avatar: commData.c__avatar || '',
                        unread_count: 0, // Default to 0 for restored windows
                        message_count: 0 // Default to 0 for restored windows
                    };
                } catch (error) {
                    console.error('💾 App: Failed to fetch comm:', commId, error);
                    return null;
                }
            }
        );
    } catch (error) {
        console.error('💾 App: Failed to restore comm windows:', error);
    }
};

onMounted(async () => { 
    try {
        // 1. Initialize the client instance ID
        const clientId = fetchClientInstanceId();
        
        // 2. Initialize the auth store
        await authStore.initialize();
        
        // 3. Get HTTP client and inject auth store
        const httpClient = getHttpClient(authStore);
        await httpClient.initialize();
        
        // 4. Initialize the user store
        await userStore.initialize();
        
        // 5. Initialize page history store first
        pageHistoryStore.initialize();
        
        // 6. Initialize page history tracking
        initializeTracking();
        
        // 7. Restore comm windows from localStorage (after auth and user are initialized)
        if (authStore.isAuthenticated) {
            await restoreCommWindows();
        }
        
    } catch (error) {
        console.error('Failed to initialize application:', error);
    } finally {
        loading.value = false;
    }
});

// Function to close claim notification toast
const closeClaimNotification = (message: any) => {
    // Remove the specific message from the claim-notifications group
    toast.removeGroup('claim-notifications');
};

const closePhoneAnswerNotification = (message: any) => {
    // Remove the specific message from the phone-answer-notifications group
    toast.removeGroup('phone-answer-notifications');
};
</script>

<template>
    <Toast position="bottom-left" data-testid="toast-notification" :closable="true" />
    
    <!-- Toast for validation messages -->
    <Toast position="bottom-left" group="validation-messages" data-testid="validation-toast" :closable="true" />
    
    <!-- Special toast for claim notifications with custom template -->
    <Toast position="bottom-left" group="claim-notifications" data-testid="toast-notification">
      <template #message="{ message }">
        <div class="flex flex-col items-start flex-auto">
          <div class="flex items-center gap-2 mb-2">
            <i class="pi pi-info-circle text-blue-500"></i>
            <span class="font-bold">{{ message.summary }}</span>
          </div>
          <div class="mb-3">{{ message.detail.split('|')[0] }}</div>
          <router-link 
            :to="message.detail.split('|')[1]"
            class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium no-underline"
            @click="closeClaimNotification(message)"
          >
            {{ message.summary === 'New Chat Ready to Claim' ? 'View Case' : 'View Cases' }}
          </router-link>
        </div>
      </template>
    </Toast>
    
    <!-- Special toast for phone answer notifications with custom template -->
    <Toast position="bottom-left" group="phone-answer-notifications" data-testid="phone-answer-toast-notification">
      <template #message="{ message }">
        <div class="flex flex-col items-start flex-auto">
          <div class="flex items-center gap-2 mb-2">
            <i class="pi pi-phone text-green-500"></i>
            <span class="font-bold">{{ message.summary }}</span>
          </div>
          <div class="mb-3">{{ message.detail.split('|')[0] }}</div>
          <router-link 
            :to="message.detail.split('|')[1]"
            class="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm font-medium no-underline"
            @click="closePhoneAnswerNotification(message)"
          >
            Access Case
          </router-link>
        </div>
      </template>
    </Toast>
    
    <!-- Toast for bravo-actions (with action buttons) -->
    <BravoToast group="bravo-actions" position="bottom-left" />
    
          <TestDomainBanner />
          <PreviewBanner />
    <div class="app">
        <Message v-if="!isOnline" severity="warn" :closable="false" class="offline-banner">
            <i class="pi pi-wifi" style="margin-right: 0.5rem"></i>
            You are currently offline. Some functionality may not work as expected.
        </Message>
        <Message v-if="hasServerError" severity="error" :closable="false" class="server-error-banner">
            <i class="pi pi-exclamation-triangle" style="margin-right: 0.5rem"></i>
            We are currently experiencing system issues. Please check our 
            <a href="https://status.goboomtown.com/" target="_blank" rel="noopener noreferrer" class="status-page-link">
                status page
            </a>
            and try again in a few minutes.
        </Message>
        <div v-if="loading" class="app-loading">
            <LoadingLogo />
        </div>
        <template v-else>
            <!-- <LocalizedMessage /> -->
            <RouterView />
        </template>

        <!-- Development tools -->
        <ApiResponseViewer ref="apiViewerRef" :hideButton="true" />
        <MockApiConfigurator :apiViewerRef="apiViewerRef" />
        <!-- <DebugTools /> -->
        
    </div>
</template>

<style scoped>
.app {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}

.app-header {
    display: flex;
    justify-content: flex-end;
    padding: 0.5rem 1rem;
    background-color: var(--surface-100, #f8f9fa);
    border-bottom: 1px solid var(--surface-200, #e9ecef);
}

.app-loading {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 100px;
}

.offline-banner,
.server-error-banner {
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    margin: 0;
    border-radius: 0;
}

/* Responsive improvements for error banners */
@media (max-width: 768px) {
    .offline-banner,
    .server-error-banner {
        font-size: 0.875rem;
        padding: 0.5rem;
        line-height: 1.4;
    }
    
    .server-error-banner .status-page-link {
        display: inline-block;
        margin-top: 0.25rem;
        word-break: break-all;
    }
}

@media (max-width: 480px) {
    .offline-banner,
    .server-error-banner {
        font-size: 0.8rem;
        padding: 0.4rem;
        text-align: center;
    }
    
    .server-error-banner .status-page-link {
        font-size: 0.8rem;
        margin-top: 0.5rem;
        display: block;
    }
    
    .offline-banner i,
    .server-error-banner i {
        display: block;
        margin: 0 0 0.25rem 0;
    }
}

.status-page-link {
    color: #1e40af;
    text-decoration: underline;
    font-weight: 600;
}

.status-page-link:hover {
    color: #1e3a8a;
    text-decoration: none;
}

.initializing {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: var(--surface-50);
}

.loading-text {
    margin-top: 1rem;
    color: var(--surface-600);
    font-size: 0.875rem;
}

.logo {
    margin: 2rem auto;
}

nav {
    font-size: 14px;
    margin-top: 2rem;
}

nav a.router-link-exact-active {
    color: var(--color-text);
}

nav a.router-link-exact-active:hover {
    background-color: transparent;
}

nav a {
    display: inline-block;
    margin-right: 1rem;
    border-left: 1px solid var(--color-border);
}

/* Banner specific overrides (keeping position: fixed for both banners) */
.offline-banner,
.server-error-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 10000;
    border-radius: 0;
    text-align: center;
    padding: 0.75rem 1rem;
    white-space: normal;
    word-wrap: break-word;
}

.offline-banner {
    background-color: var(--yellow-100);
    border-bottom: 1px solid var(--yellow-200);
    color: var(--yellow-900);
}
</style>
